import { Store } from "../utils/Store";
import LoginStore from "./LoginStore";
import SessionState from "./SessionState";
import type { LoginStateType } from "./SessionState";
import Tracking from "../tracking/Tracking";
import { LoginStoreActions } from "../actions/LoginStoreActions";
import { LoginActions } from "../actions/LoginActions";
import { LogoutActions } from "../actions/LogoutAction";
import RequestUtil from "../utils/RequestUtil";
import CountryUtil from "../utils/CountryUtil";
import { ClientSession } from "../utils/ClientSessionHolder";
import liteguid from "lite-guid";
import GdprStorage from "../storage/GdprStorage";
import type { GdprCategory } from "../storage/GdprStorage";
import { isEmpty, stringOrEmptyString } from "../../utils/utilFunctions";
import LoginUtil from "../utils/LoginUtil";
import type { Jwts } from "../utils/LoginUtil";
import * as BettyJwt from "../../model/BettyJwt";
import { FeatureActions } from "./FeatureSessionStore";
import isDeepEqual from "fast-deep-equal"; //eslint-disable-line
let addressDeprecationLogged = false;

/**
 * The session store is the place where all logins are handled.
 * You can listen to the store. You will then be triggered:
 * - when a user logs in successfully
 * - when a user fails to login
 * - when a user logs out
 * - when an employee selects a customer
 *
 * Usually, accessor methods to the state (like getCustomersStores) will throw exceptions if there is no customer
 * (not logged in).
 */
type SessionStoreState = {
	failed: boolean;
	loggedIn: boolean;
	notLoggedin: boolean;
	employeeRequiredRedirect: boolean;
	timedOut: boolean;
	pending: boolean;
	failReason: string | null | undefined;
	failedStatusCode: number | null | undefined;
	waitingForIdamLogout: boolean;
};

class SessionStoreClass extends Store {
	constructor() {
		super();
		this.init();
	}

	init() {
		GdprStorage.validateLocalstorage();
		GdprStorage.removeNotAllowedCookies();
		LoginStore.init(this);
		SessionState.setEmptyState();
		SessionState.setAnonymousUserId(this.setAnonymousUserId());
		this.handleSitecoreCookieChanges();
		this.listenTo(LoginStoreActions.LoginCompleted, this.onLoginCompleted);
		this.listenTo(LoginStoreActions.LoginStateChanged, this.triggerState);
		// We need to rerun this once the (anonymous) userId has been initialized
		FeatureActions.recalculateMemberships.trigger();
	}

	start() {
		LoginStore.start();
	}

	handleSitecoreCookieChanges() {
		//sync sitecore store id to locale storage for anonymous
		const sitecoreStoreId = this.getSitecoreStoreId();

		if (sitecoreStoreId) {
			const bettyStoreId = this.mapToBettyStoreId(sitecoreStoreId);
			const selectedStoreId = this.getSelectedStoreId();

			// Don't reset selected store if we are in the same store
			// Setting the selected store will always remove the selected delivery address
			// When setting the delivery address, the sitecore and betty storeId is also set
			if (bettyStoreId && selectedStoreId !== bettyStoreId) {
				// in mario countries update the store id only if it is online visible
				// in non mario country always update selected store id
				if (!this.isMarioCountry() || this.isOnlineVisible(bettyStoreId)) {
					// update the localstorage field for anon user
					this.updateStoreIdForCustomerInCookie(null, bettyStoreId);
					this.setSelectedStoreId(bettyStoreId);
					this.setLastKnownSitecoreStoreId(sitecoreStoreId);
				}
			}
		}
	}

	isOnlineVisible(bettyStoreId: string): boolean {
		return SessionState.getState().onlineVisibleStores?.has(bettyStoreId) ?? false;
	}

	/**
	 * Takes random UUID from cookie. If not set yet, generates a UUID and uses it from there on.
	 */
	setAnonymousUserId(): string {
		const currentAnonymousUserId = GdprStorage.cookie.read("anonymousUserId");

		if (!currentAnonymousUserId) {
			const uuid = liteguid.create();
			// 1 * 365 * 24 * 60 * 60 * 1000 leads to being the cookie valid 1 year from now.
			const expirationDate = new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000);
			GdprStorage.cookie.put("anonymousUserId", uuid, GdprStorage.NECESSARY, {
				expires: expirationDate,
			});
			Tracking.trackAnonymous();
			return uuid;
		}

		return currentAnonymousUserId;
	}

	/**
	 * Maps the given sitecorestoreid to a betty store id, if that store is onlinevisible.
	 * @param sitecoreStoreId
	 * @returns betty store id
	 */
	mapToBettyStoreId(sitecoreStoreId: string): string | null | undefined {
		return sitecoreStoreId && !isEmpty(SessionState.getState().sitecore2BettyStoreIdMapping)
			? SessionState.getState().sitecore2BettyStoreIdMapping![sitecoreStoreId]
			: undefined;
	}

	/**
	 * Maps the given betty store id to a sitecore store id.
	 * @param bettyStoreId
	 * @returns sitecore store id
	 */
	mapToSitecoreStoreId(bettyStoreId: string | null | undefined): string | null | undefined {
		return bettyStoreId && !isEmpty(SessionState.getState().betty2SitecoreStoreIdMapping)
			? SessionState.getState().betty2SitecoreStoreIdMapping![bettyStoreId]
			: undefined;
	}

	getSitecoreStoreId(): string | null | undefined {
		const sitecoreCookieContent = GdprStorage.cookie.read("UserSettings") || "";
		const regex = new RegExp(/SelectedStore=\{?([\d\w\-]+)\}?/, "g"); //eslint-disable-line
		const matches = regex.exec(sitecoreCookieContent);

		if (matches && matches.length >= 2) {
			return matches[1].toLowerCase();
		} else {
			return null;
		}
	}

	setSitecoreStoreId(storeId: string | null | undefined, deliveryAddressId: string | null | undefined) {
		if (storeId) {
			storeId = storeId.toLowerCase();
		}

		if (deliveryAddressId) {
			deliveryAddressId = deliveryAddressId.toLowerCase();
		}

		this.setLastKnownSitecoreStoreId(storeId);
		const expirationDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
		let userSettingsString = "SelectedStore=" + stringOrEmptyString(storeId);

		if (deliveryAddressId != null) {
			userSettingsString =
				"SelectedStore=" + stringOrEmptyString(storeId) + "&SelectedAddress=" + deliveryAddressId;
		}

		GdprStorage.cookie.put("UserSettings", userSettingsString, GdprStorage.NECESSARY, {
			domain: SessionState.getCookieDomain(),
			expires: expirationDate,
			secure: true,
		});
	}

	getCookieDomain(): string {
		return SessionState.getCookieDomain();
	}

	getLastKnownSitecoreStoreId(): string | null | undefined {
		return GdprStorage.localStorage.read("lastKnownSitecoreStoreId");
	}

	setLastKnownSitecoreStoreId(sitecoreStoreId: string | null | undefined) {
		GdprStorage.localStorage.put("lastKnownSitecoreStoreId", sitecoreStoreId, GdprStorage.FUNCTIONAL);
	}

	getLastKnownForceReloadTimestamp(): string | null | undefined {
		return GdprStorage.localStorage.read("lastKnownForceReloadTimestamp");
	}

	setLastKnownForceReloadTimestamp(forceReloadTimestamp: string) {
		GdprStorage.localStorage.put("lastKnownForceReloadTimestamp", forceReloadTimestamp, GdprStorage.NECESSARY);
	}

	/**
	 * Takes selected storeId from cookie. If none is set yet, will return none (this is to ensure that
	 * customers see the information before a store (even the default store) is set in the cookie.
	 */
	getSelectedStoreId(): string | null | undefined {
		const address = SessionState.getSelectedAddress(this.getCustomerId());
		const anon_address = SessionState.getSelectedAddress(null);
		return address ? address.storeId : anon_address ? anon_address.storeId : null;
	}

	/**
	 * Updates the cookie, deletes the selected address and
	 * sets the passed store id for the customer.
	 * @param customerId
	 * @param storeId
	 */
	updateStoreIdForCustomerInCookie(customerId: string | null | undefined, storeId: string) {
		const customerAddress = {
			addressId: null,
			addressHash: null,
			storeId: storeId,
		};
		SessionState.setSelectedAddress(customerId, customerAddress);
	}

	/**
	 * sets the selected store in the cookie and triggers SessionStore afterwards.
	 */
	setSelectedStoreId(storeId: string) {
		this.updateStoreIdForCustomerInCookie(this.getCustomerId(), storeId);
		// We need to also set the anonymous cookie for consistency reasons.
		this.updateStoreIdForCustomerInCookie(null, storeId);
		const state = SessionState.getState();
		const storeOrAddressChanged = state.customerStoreId !== storeId || state.deliveryAddress !== null;
		SessionState.setDeliveryAddress(null);
		SessionState.setCustomerStoreId(storeId);
		const sitecoreStoreId = this.mapToSitecoreStoreId(storeId) || null;
		// Delete the sitecore cookie, if we don't find the store, as it's probably an address in a non CCOV store
		// If we don't delete it, our store and the sitecore store differ, so we will overwrite our store and address on the next reload.
		this.setSitecoreStoreId(sitecoreStoreId, null);

		if (storeOrAddressChanged && (this.isMarioCountry() || this.loggedIn())) {
			this.triggerState();
		}
	}

	/**
	 * Returns the currently set Country as determined by the CountryUtil
	 */
	getCountry() {
		return CountryUtil.getCountry();
	}

	/**
	 * Returns the currently set sales line as determined by the CountryUtil
	 */
	getSalesLine() {
		return SessionState.getState().salesLine;
	}

	/**
	 * Determines if a country is fulfilled via MCRM, not Betty.
	 */
	isMcrmCountry(): boolean {
		return CountryUtil.isMcrmCountry();
	}

	/**
	 * Returns anonymousUserId set in cookie.
	 */
	getAnonymousUserId() {
		return SessionState.getState().anonymousUserId;
	}

	/**
	 * Returns Mario is activated for the current country.
	 */
	isMarioCountry(): boolean {
		return CountryUtil.isMarioCountry();
	}

	/**
	 * Returns whether WEB customers should be taken from CDMCore via the alexcustomerdata service.
	 * "True" means take data from CRMCore.
	 */
	returnsAlexCustomersInOldCustomerdata(): boolean {
		return CountryUtil.returnsAlexCustomersInOldCustomerdata();
	}

	/**
	 * Returns whether customer data should be taken from non-MCRM data sources via alexcustomerdata service.
	 * "True" means take data from non-MCRM sources.
	 */
	isCustomersFromNonMCRMSources(): boolean {
		return CountryUtil.isCustomersFromNonMCRMSources();
	}

	/**
	 * Returns wether combi orders are activated for the current country.
	 */
	isCombiOrderCountry(): boolean {
		return CountryUtil.isCombiOrderCountry();
	}

	/**
	 * Returns whether IDAM login is activated for the current country.
	 */
	shouldUseIdamLogin(): boolean {
		return LoginStore.shouldUseIdamLogin();
	}

	getIdamLoginBaseUrl(): string | null | undefined {
		return LoginStore.getIdamLoginBaseUrl();
	}

	/**
	 * Returns the currently set locale as determined by the backend
	 */
	getLocale() {
		return SessionState.getState().locale;
	}

	/**
	 * Returns the id (string) of the logged in customer - or the id of the currently selected customer when logged in as employee -
	 * or null if neither.
	 * A natural person is identified by a customerId + cardholderNumber.
	 */
	getCustomerId(): string | null | undefined {
		return SessionState.getState().customerId;
	}

	/**
	 * Returns the number (string) of the logged in cardholder - or the primary cardholder number of the currently selected customer when logged in as employee -
	 * or null if neither.
	 * A natural person is identified by a customerId + cardholderNumber.
	 */
	getCardholderNumber(): string | null | undefined {
		return SessionState.getState().cardholderNumber;
	}

	/**
	 * Returns the selected store id (string) of the customer - or null if not logged in or no customer selected.
	 */
	getCustomerStore(): string | null | undefined {
		return SessionState.getCustomerStore();
	}

	// Can be null
	getDefaultStoreForAnon(): string | null | undefined {
		return CountryUtil.getCountryConfig().defaultStore;
	}

	/**
	 * Returns the id (string) of the logged in employee or null if not logged in as employee.
	 */
	getEmployeeId(): any {
		const state = SessionState.getState();
		return state.employeeData ? state.employeeData.upn : null;
	}

	/**
	 * returns true if the logged in user is an employee.
	 */
	isEmployee(): boolean {
		return !!this.getEmployeeId();
	}

	/**
	 * returns true if the logged in user is an customer - returns false if the logged in user is an employee,
	 * regardless of the currently selected customer..
	 */
	isCustomer(): boolean {
		return !!this.getCustomerId() && !this.isEmployee();
	}

	/**
	 * returns true if the user is logged in.
	 */
	loggedIn(): boolean {
		return SessionState.loggedIn();
	}

	/**
	 * returns true if the user is not logged in.
	 * NOTE: this is not just "!loggedIn()"!
	 * This returns true, when after the initial page load and cookie checks
	 * it turns out that the user is not logged in (aka anonymous user).
	 */
	notLoggedIn(): boolean {
		return LoginStore.getState() === "notLoggedin";
	}

	/**
	 * returns true if the latest login has failed
	 */
	failed(): boolean {
		return LoginStore.getState() === "failed";
	}

	/**
	 * Returns a string indicating the reason for a failed login if the latest login has failed. Undefined otherwise.
	 */
	failReason(): string | null | undefined {
		return LoginStore.failReason();
	}

	/**
	 * Returns status code of failed call, otherwise undefined.
	 */
	failedStatusCode(): number | null | undefined {
		return LoginStore.failedStatusCode();
	}

	timedOut(): boolean {
		return LoginStore.getState() === "timedOut";
	}

	pending(): boolean {
		return SessionState.pending();
	}

	/**
	 * returns the state of the Sessionstore.
	 */
	makeState(): SessionStoreState {
		return {
			failed: this.failed(),
			loggedIn: this.loggedIn(),
			notLoggedin: this.notLoggedIn(),
			employeeRequiredRedirect: false,
			timedOut: this.timedOut(),
			pending: this.pending(),
			failReason: this.failReason(),
			failedStatusCode: this.failedStatusCode(),
			waitingForIdamLogout: LoginStore.getWaitingForIdamLogout(),
		};
	}

	triggerState: (...args: Array<any>) => any = () => {
		this.trigger(this.makeState());
	};

	getIdamAccessToken(): string | null | undefined {
		return LoginStore.getIdamAccessToken();
	}

	switchBusinessAccount(newAccountId: string) {
		LoginStore.switchBusinessAccount(newAccountId);
	}

	hasDeliveryAddressForStore(uStores: string, storeId: string): boolean {
		if (!uStores) {
			return false;
		}

		const storeNumbers = uStores.replace(/[^\d,]/g, "").split(",");
		const storeIds = storeNumbers.map(storeNumber => storeNumber.padStart(5, "0"));
		return storeIds.includes(storeId);
	}

	onLoginCompleted: (...args: Array<any>) => any = jwtDecoded => {
		SessionState.setAuthorizationData({
			entitlements: jwtDecoded.entitlements,
			storeEnts: jwtDecoded.storeEnts,
			uStoreEnts: jwtDecoded.uStoreEnts,
			uStores: jwtDecoded.uStores,
		});

		if (BettyJwt.isEmployee(jwtDecoded)) {
			SessionState.setEmployeeData(jwtDecoded.payload);
			const savedCustomerId = SessionState.getSelectedCustomerId();
			const savedCardholderNumber = SessionState.getSelectedCardholderNumber();
			SessionState.setCustomerId(null);
			SessionState.setCardholderNumber(null);

			try {
				if (!!savedCustomerId && !!savedCardholderNumber) {
					SessionState.setSelectedCustomerId(savedCustomerId, savedCardholderNumber);
				}
			} catch (error) {
				SessionState.setCustomerId(null);
				SessionState.setCardholderNumber(null);
				console.error("could not load selected customer", error);
			}
		} else {
			SessionState.setCustomerTierLevel(BettyJwt.getTierLevel(jwtDecoded));
			SessionState.setCustomerId(BettyJwt.getUpn(jwtDecoded));
			SessionState.setCardholderNumber(BettyJwt.getCardholderNumber(jwtDecoded));
			SessionState.setEmployeeData(null);
			const anonAddress = SessionState.getSelectedAddress(null);
			const anonStoreId = anonAddress?.storeId;

			if (anonStoreId) {
				const selectedAddress = SessionState.getSelectedAddress(this.getCustomerId());
				const customerStoreId = selectedAddress?.storeId;

				if (anonStoreId !== customerStoreId) {
					if (this.isMarioCountry() || this.hasDeliveryAddressForStore(jwtDecoded.uStores, anonStoreId)) {
						SessionState.removeSelectedAddress(this.getCustomerId());
					}
				}
			}
		}

		this.handleSitecoreCookieChanges();
		this.triggerState();
	};

	shutdownForExternalRedirect() {
		LoginStore.shutdownForExternalRedirect();
	}

	loginWasInternal(): boolean {
		return LoginStore.loginWasInternal();
	}

	wasLoggedIntoIdam(): boolean {
		return this.getIdamAccessToken() !== undefined;
	}

	// Necessary for Idam Error Page (LoginParkingPage.js)
	getIdamLogoutUrl(): string | null | undefined {
		return LoginStore.getIdamLogoutUrl();
	}

	validateCustomerId(customerId: string) {
		if (!customerId || customerId.indexOf("emp") !== -1) {
			throw new Error("customerId has wrong value: '" + customerId + "'");
		}
	}

	/**
	 * For internal use only! (and for customer selection)
	 */
	setSelectedCustomerId(newCustomerId: string, newCardholderNumber: string) {
		this.validateCustomerId(newCustomerId);
		SessionState.setSelectedCustomerId(newCustomerId, newCardholderNumber);
		// Force to trigger in delivery address setting.
		this.setDeliveryAddress(null, true); // sets also customer store
	}

	/**
	 * For internal use only! (and for customer deselection)
	 */
	clearSelectedCustomerId() {
		if (!this.isEmployee()) {
			throw "only an employee can clear a selected customer";
		}

		SessionState.setSelectedCustomerId(null, null);
		// Force to trigger in delivery address setting.
		this.setDeliveryAddress(null, true); // sets also customer store
	}

	/**
	 * We use a hash for the address, because we do not have a real ID.
	 * However, for our purposes (saving, which address was selected, and pre-selecting it),
	 * this is not a problem.
	 * @Deprecated
	 */
	hasAddressHash(): boolean {
		const addressHash = this.getSelectedDeliveryAddressHash();
		return !!addressHash;
	}

	/**
	 * This is the 'real' ID.
	 */
	hasAddressId(): boolean {
		const addressId = this.getSelectedDeliveryAddressId();
		return !!addressId;
	}

	setDeliveryAddress(address: Record<string, any> | null | undefined, force: boolean) {
		if (address && address.integrationId) {
			if (this._isFishyAddressId(address)) {
				Tracking.trackClientErrorEvent({
					component: "SessionStore",
					message: `Setting delivery address to something with a weird id: ${address.integrationId}\n${
						new Error().stack
					}`,
				});
			}
		}

		if (isDeepEqual(SessionState.getState().deliveryAddress, address) && !force) {
			return;
		}

		const newAddressHash = address ? address.hash : null;
		const newAddressId = address ? address.integrationId : null;
		const newStoreId = address ? address.deliveryStore : null;
		const customerAddress = {
			addressId: newAddressId,
			addressHash: newAddressHash,
			storeId: newStoreId,
		};
		SessionState.setSelectedAddress(this.getCustomerId(), customerAddress);

		// We need to also set the anonymous cookie for consistency reasons.
		if (newStoreId) {
			this.updateStoreIdForCustomerInCookie(null, newStoreId);
		}

		const sitecoreStoreId = this.mapToSitecoreStoreId(newStoreId) || null;
		// Delete the sitecore cookie, if we don't find the store, as it's probably an address in a non CCOV store
		// If we don't delete it, our store and the sitecore store differ, so we will overwrite our store and address on the next reload.
		this.setSitecoreStoreId(sitecoreStoreId, stringOrEmptyString(newAddressId));
		const state = SessionState.getState();
		const oldAddressId = state.deliveryAddress ? state.deliveryAddress.integrationId : null;
		let shouldTrigger = false;

		if (!state.deliveryAddress || oldAddressId !== newAddressId || state.customerStoreId !== newStoreId) {
			shouldTrigger = true;
		}

		SessionState.setDeliveryAddress(address);
		SessionState.setCustomerStoreId(newStoreId);

		if (shouldTrigger || force) {
			this.triggerState();
		}
	}

	_isFishyAddressId(address: Record<string, any>): boolean {
		return !/^\d+-/.test(address.integrationId);
	}

	getState(): LoginStateType {
		return LoginStore.getState();
	}

	/**
	 * @Deprecated the address should not be saved here anyway!
	 */
	getSelectedDeliveryAddress(): Record<string, any> | null | undefined {
		if (!addressDeprecationLogged) {
			// this should be error as soon as it doesn't happen on every single page load
			console.info("SessionStore.getSelectedDeliveryAddress is deprecated!");
			addressDeprecationLogged = true;
		}

		return SessionState.getState().deliveryAddress;
	}

	getSelectedDeliveryAddressId(): string | null | undefined {
		const address = SessionState.getSelectedAddress(this.getCustomerId());
		return address?.addressId || null;
	}

	getSelectedDeliveryAddressHash(): string | null | undefined {
		const customerId = this.getCustomerId();
		const address = SessionState.getSelectedAddress(customerId);
		// TODO: Double-check if the "adrHash_[...]" cookie is really still in use
		const hash: string | null | undefined = address
			? address.addressHash
			: SessionState.getSelectedAddressHash(customerId);

		if (hash === undefined) {
			return null;
		}

		return hash;
	}

	getAuthorizationData() {
		return SessionState.getState().authorizationData;
	}

	getCustomerTierLevel(): string | null | undefined {
		return SessionState.getState().customerTierLevel;
	}

	getJwtFromCookieAndStorage(): Jwts {
		return LoginUtil.getJwtFromCookieAndStorage();
	}

	/**
	 * Gets the session id which is generated for every initialization of the SessionStore.
	 * This means we can't track sessions over reload, but it's better than nothing.
	 */
	getClientSessionId(): string | null | undefined {
		return ClientSession.getClientSessionId();
	}

	generateSessionedCallTreeIdWithPrefix(prefix: string): string {
		return ClientSession.generateSessionedCallTreeIdWithPrefix(prefix);
	}

	getIdamError(): Record<string, any> | null | undefined {
		return LoginStore.getIdamError();
	}

	/**
	 * Checks whether a specific category of cookies has been accepted by the user.
	 */
	isCookieCategoryAllowed(gdprCategory: GdprCategory): boolean {
		try {
			// Even, if we get "undefined" back, we want to return a boolean value
			return GdprStorage.isCategoryAllowed(gdprCategory, "", "") || false;
		} catch {
			return false;
		}
	}
}

const SessionStore: SessionStoreClass = new SessionStoreClass();

/**
 * Track all failed AJAX calls
 *
 */
const trackFailedCalls = function () {
	// Parse URL
	const domParser = document.createElement("a");
	domParser.href = this.responseURL;
	const pathname = domParser.pathname.replace(/\//g, "").split(".");
	const status = this ? this.status : null;

	if (status && status !== 0 && (status < 200 || status >= 300) && pathname.length >= 2) {
		const structuredData = {
			tags: {
				url: this.responseURL,
				httpStatus: status,
				loginState: SessionStore.getState(),
			},
		};
		Tracking.trackStructuredEvent(pathname[0], pathname[1], "technical.failedCall", structuredData);
	}
};

RequestUtil.addXMLRequestCallback(trackFailedCalls);
const SessionStoreModule = {
	SessionStore,
	LoginCustomerAction: LoginActions.LoginCustomer,
	LoginEmployeeAction: LoginActions.LoginEmployee,
	LogoutAction: LogoutActions.Logout,
	LogoutManualAction: LogoutActions.LogoutManual,
	trackFailedCalls,
};
export default SessionStoreModule;
